import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProfile } from '../../store/slices/profileSlice';
import { 
  getCategoriesApi, 
  createCommunityApi, 
  getCommunityByIdApi, 
  updateCommunityApi 
} from '../../api/communities';
import './CreateCommunity.scss';

const COMMUNITY_TYPES = [
  { id: 'free', title: 'Free (Lifetime)', desc: 'Join anytime. No cost.' },
  { id: 'freetrial', title: 'Free Trial', desc: '7 days free. Paid monthly after.' },
  { id: 'monthly', title: 'Monthly Subscription', desc: 'Paid monthly. Required to join.' },
  { id: 'onetime', title: 'One-Time Payment', desc: 'Pay once. No monthly fee.' },
];

export const CreateCommunity = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const id = location.state?.id;
  const isEditMode = !!id;
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { profile, loading: profileLoading, fetched: profileFetched, error: profileError } = useSelector((state) => state.profile);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: window.location.pathname } });
    } else if (!profile && !profileLoading?.profile && !profileFetched && !profileError?.profile) {
      dispatch(fetchProfile());
    }
  }, [isAuthenticated, profile, profileLoading, profileFetched, profileError, dispatch, navigate]);

  useEffect(() => {
    if (isAuthenticated && profile && !isEditMode) {
      if (profile.isSubscriptionEnabled === true && profile.has_active_subscription !== true) {
        navigate('/profile?tab=subscriptions');
      }
    }
  }, [isAuthenticated, profile, isEditMode, navigate]);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    videoLink: '',
    type: 'free',
    subscriptionFee: '',
    trialDuration: '7',
  });

  const [coverImage, setCoverImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategoriesApi();
        if (response && response.data) {
          setCategories(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };

    fetchCategories();

    if (isEditMode) {
      const fetchCommunity = async () => {
        try {
          const response = await getCommunityByIdApi(id);
          if (response && response.data) {
            const community = response.data;
            setFormData({
              name: community.name || '',
              description: community.description || '',
              category: community.category?._id || community.category || '',
              videoLink: community.videoLink || community.media || '',
              type: community.type || 'free',
              subscriptionFee: community.subscriptionFee || '',
              trialDuration: community.trialDuration || '7',
            });
            if (community.coverImage) {
              setImagePreview(community.coverImage);
            }
          }
        } catch (err) {
          setError('Failed to load community details.');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchCommunity();
    }
  }, [id, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNext = () => {
    if (!formData.name) {
      setError('Community name is required');
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      data.append('type', formData.type);
      data.append('category', formData.category);
      data.append('videoLink', formData.videoLink);
      
      if (coverImage) {
        data.append('coverImage', coverImage);
      }

      if (formData.type !== 'free') {
        data.append('subscriptionFee', formData.subscriptionFee);
        if (formData.type === 'freetrial') {
          data.append('trialDuration', formData.trialDuration);
        }
      }

      if (isEditMode) {
        await updateCommunityApi(id, data);
      } else {
        await createCommunityApi(data);
      }

      navigate('/communities');
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="form-step step-1">
      <Form.Group className="mb-4">
        <Form.Label>Community Name</Form.Label>
        <Form.Control
          type="text"
          name="name"
          placeholder="Community Name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Describe your community</Form.Label>
        <Form.Control
          as="textarea"
          name="description"
          rows={4}
          placeholder="What is this community about?"
          value={formData.description}
          onChange={handleInputChange}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Upload Community Cover</Form.Label>
        <div className="image-upload-wrapper">
          <label htmlFor="coverImageInput" className="upload-placeholder">
            {imagePreview ? (
              <img src={imagePreview} alt="Preview" />
            ) : (
              <>
                <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M90 10H10C4.5 10 0 14.5 0 20V80C0 85.5 4.5 90 10 90H90C95.5 90 100 85.5 100 80V20C100 14.5 95.5 10 90 10ZM90 80H10V20H90V80ZM50 70L70 45L85 64H15L35 37L50 56V70Z" fill="currentColor"/>
                </svg>
                <span>Click to upload image</span>
              </>
            )}
            <input
              type="file"
              id="coverImageInput"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
          </label>
        </div>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Community Category</Form.Label>
        <Form.Select
          name="category"
          value={formData.category}
          onChange={handleInputChange}
        >
          <option value="">Select Category</option>
          {categories.map((cat) => (
            <option key={cat._id} value={cat._id}>
              {cat.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Add Cover Video Link</Form.Label>
        <Form.Control
          type="text"
          name="videoLink"
          placeholder="https://..."
          value={formData.videoLink}
          onChange={handleInputChange}
        />
      </Form.Group>

      <Button className="btn-submit" onClick={handleNext}>
        Continue
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="form-step step-2">
      <div className="type-options mb-4">
        {COMMUNITY_TYPES.map((type) => (
          <div
            key={type.id}
            className={`type-option-card ${formData.type === type.id ? 'selected' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
          >
            <div className="radio-circle"></div>
            <div className="option-info">
              <span className="option-title">{type.title}</span>
              <span className="option-desc">{type.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {(formData.type === 'monthly' || formData.type === 'onetime' || formData.type === 'freetrial') && (
        <Form.Group className="mb-4">
          <Form.Label>Subscription Fee ($)</Form.Label>
          <Form.Control
            type="number"
            name="subscriptionFee"
            placeholder="Enter amount"
            value={formData.subscriptionFee}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      )}

      {formData.type === 'freetrial' && (
        <Form.Group className="mb-4">
          <Form.Label>Trial Duration (Days)</Form.Label>
          <Form.Control
            type="number"
            name="trialDuration"
            placeholder="e.g. 7"
            value={formData.trialDuration}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
      )}

      <div className="preview-msg">
        {formData.type === 'free' && 'Users will get free lifetime access to this community.'}
        {formData.type === 'freetrial' && `Users will get ${formData.trialDuration || 7} days free, then pay $${formData.subscriptionFee || 0} monthly.`}
        {formData.type === 'monthly' && `Users will pay $${formData.subscriptionFee || 0} monthly to join.`}
        {formData.type === 'onetime' && `Users will pay $${formData.subscriptionFee || 0} once for lifetime access.`}
      </div>

      <Button className="btn-submit" onClick={handleSubmit} disabled={loading}>
        {loading ? <Spinner animation="border" size="sm" /> : (isEditMode ? 'Update Community' : 'Create Community')}
      </Button>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="create-community-page d-flex align-items-center justify-content-center">
        <Spinner animation="border" color="primary" />
      </div>
    );
  }

  return (
    <div className="create-community-page">
      <Container>
        <div className="form-container">
          <div className="step-header">
            <Link to="#" onClick={(e) => { e.preventDefault(); handleBack(); }} className="back-btn">
              <i className="bi bi-arrow-left"></i>
            </Link>
            <h1>{step === 1 ? (isEditMode ? 'Edit Community' : 'Create Community') : 'Type Of Community'}</h1>
          </div>

          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

          {step === 1 ? renderStep1() : renderStep2()}
        </div>
      </Container>
    </div>
  );
};
